'use client';
import React, { useState } from 'react';
import HomepageNav from '@/app/components/HomepageNav';
import HomepageFooter from '@/app/components/HomepageFooter';
import { Loader2, CheckCircle, Mail, MessageSquare } from 'lucide-react';

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    // In production, this would send to an email service
    await new Promise((r) => setTimeout(r, 1000));
    setSent(true);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <HomepageNav />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-screen-xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">Contact Us</h1>
          <p className="text-slate-300 text-base sm:text-lg max-w-xl mx-auto">
            Have a question or need help? We&apos;re here for you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="space-y-6">
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-border">
              <Mail size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-foreground mb-1">Email Support</div>
                <a href="mailto:support@mydesignhubai.com" className="text-sm text-primary hover:underline">support@mydesignhubai.com</a>
                <p className="text-xs text-muted-foreground mt-1">We respond within 24 hours on business days.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-card border border-border">
              <MessageSquare size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-foreground mb-1">General Inquiries</div>
                <a href="mailto:hello@mydesignhubai.com" className="text-sm text-primary hover:underline">hello@mydesignhubai.com</a>
                <p className="text-xs text-muted-foreground mt-1">For partnerships, press, and general questions.</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-3xl p-6 sm:p-8">
            {sent ? (
              <div className="text-center py-8">
                <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
                <h2 className="text-lg font-bold text-foreground mb-2">Message sent!</h2>
                <p className="text-sm text-muted-foreground">We&apos;ll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Name</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name"
                      className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/25" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com"
                      className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/25" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Subject</label>
                  <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="How can we help?"
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/25" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Message</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={4} placeholder="Tell us more..."
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/25 resize-none" />
                </div>
                {error && <p className="text-xs text-red-400">{error}</p>}
                <button type="submit" disabled={isLoading}
                  className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {isLoading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <HomepageFooter />
    </div>
  );
}
